import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../ui/Button/Button';
import { FormInput } from '../../../ui/FormInput/FormInput';
import { ValidationMessage } from '../../../components/Forms/ValidationMessage';
import { replyToEmailService } from '../../../services/replyToEmailService';
import { HttpError } from '../../../services/apiClient';
import { ApiErrorCode } from '../../../services/apiErrorCodes';
import { validateEmail } from '../../../utils/validators';
import { TranslationKey } from '../../../i18n/translationKeys';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export const ReplyToEmailPanel: React.FC = () => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const [value, setValue] = useState('');
    const [defaultReplyToEmail, setDefaultReplyToEmail] = useState<string | null>(null);
    const [saveState, setSaveState] = useState<SaveState>('idle');
    const [invalid, setInvalid] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const setting = await replyToEmailService.get();
                if (cancelled) return;
                setValue(setting.replyToEmail ?? '');
                setDefaultReplyToEmail(setting.defaultReplyToEmail);
                setLoaded(true);
            } catch (e) {
                console.error('Failed to load the customer reply email:', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!loaded) return null;

    const handleSave = async () => {
        const trimmed = value.trim();
        if (trimmed && !validateEmail(trimmed)) {
            setInvalid(true);
            return;
        }
        setInvalid(false);
        setSaveState('saving');
        try {
            await replyToEmailService.set(trimmed || null);
            setSaveState('saved');
        } catch (e) {
            // The server rule is stricter than validateEmail (length, ? & # %), so its rejection reads as invalid input.
            if (e instanceof HttpError && e.code === ApiErrorCode.ReplyToEmailInvalid) {
                setInvalid(true);
                setSaveState('idle');
                return;
            }
            setSaveState('error');
            console.error('Failed to save the customer reply email:', e);
        }
    };

    return (
        <div className="space-y-s">
            <h2 className="text-block-title text-content">{t(TranslationKey.Settings.ReplyToEmail)}</h2>
            <p className="text-primary-700">{t(TranslationKey.Settings.ReplyToEmailDescription)}</p>
            <div className="flex items-start gap-s pt-xs">
                <div className="min-w-0 flex-1 max-w-80">
                    <FormInput
                        id="settings-reply-to-email"
                        data-testid="settings-reply-to-email"
                        className="w-full"
                        type="email"
                        value={value}
                        placeholder={defaultReplyToEmail ?? undefined}
                        onChange={(e) => { setValue(e.target.value); setSaveState('idle'); }}
                        aria-invalid={invalid}
                        aria-describedby={invalid ? 'settings-reply-to-email-err' : undefined}
                    />
                    <ValidationMessage
                        id="settings-reply-to-email-err"
                        data-testid="settings-reply-to-email-invalid"
                        message={invalid ? t(TranslationKey.CustomerFollowUp.Email.Errors.EmailAddressInvalid) : undefined}
                    />
                </div>
                <Button data-testid="settings-reply-to-email-save" onClick={() => void handleSave()} loading={saveState === 'saving'}>
                    {t(TranslationKey.Settings.ReplyToEmailSave)}
                </Button>
            </div>
            {saveState === 'saved' && (
                <p data-testid="settings-reply-to-email-saved" className="text-primary-700 text-s">{t(TranslationKey.Settings.ReplyToEmailSaved)}</p>
            )}
            {saveState === 'error' && (
                <p data-testid="settings-reply-to-email-error" className="text-danger text-s">{t(TranslationKey.Settings.ReplyToEmailError)}</p>
            )}
        </div>
    );
};
